import React, { useMemo, useState } from "react";
import { Mail, Phone, MapPin, BadgeCheck, Copy, Zap } from "../components/Icons";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

export default function Contact() {
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [copied, setCopied] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    topic: "Partnership",
    message: "",
  });

  const emailTo = useMemo(() => "praashgreenenergy@gmail.com", []);

  const onChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailTo);
      setCopied(true);
      setStatus({ type: "success", message: "Email copied to clipboard." });
      setTimeout(() => setCopied(false), 1400);
      setTimeout(() => setStatus({ type: "idle", message: "" }), 2400);
    } catch {
      setStatus({ type: "error", message: "Couldn’t copy. Please copy manually." });
      setTimeout(() => setStatus({ type: "idle", message: "" }), 2400);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", message: "Please fill Name, Email, and Message." });
      setTimeout(() => setStatus({ type: "idle", message: "" }), 2400);
      return;
    }

    const subject = encodeURIComponent(`[${form.topic}] Inquiry — Praash Green Energy`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\nTopic: ${form.topic}\n\nMessage:\n${form.message}\n`
    );

    window.location.href = `mailto:${emailTo}?subject=${subject}&body=${body}`;

    setStatus({ type: "success", message: "Opening your email client…" });
    setTimeout(() => setStatus({ type: "idle", message: "" }), 2400);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
          <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
          Contact
        </div>

        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          Partner with Praash Green Energy
        </h1>

        <p className="mt-3 max-w-3xl text-white/65">
          We develop, build, own, and operate renewable infrastructure.
          Reach out for partnerships, land opportunities, offtake discussions, supply/vendor collaboration, or careers.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-5">
        {/* Left: info */}
        <div className="lg:col-span-2">
          <div className="rounded-3xl border border-white/10 bg-black/25 p-6 backdrop-blur">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
                <Zap className="h-5 w-5 text-white/80" />
              </div>
              <div>
                <div className="text-sm text-white/60">Operational capacity</div>
                <div className="text-lg font-semibold text-white">5 MW (Owned & Operating)</div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                <Mail className="mt-0.5 h-5 w-5 text-white/70" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white/60">Email</div>
                  <div className="mt-1 flex items-center gap-3">
                    <span className="truncate font-medium text-white">{emailTo}</span>
                    <button
                      type="button"
                      onClick={copyEmail}
                      className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-1.5 text-xs text-white/80 hover:bg-black/40"
                      title="Copy email"
                    >
                      {copied ? <BadgeCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                <MapPin className="mt-0.5 h-5 w-5 text-white/70" />
                <div>
                  <div className="text-sm text-white/60">Base</div>
                  <div className="font-medium text-white">Pune, Maharashtra, India</div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-sm leading-relaxed text-white/65">
              Typical topics: <span className="text-white/80">land</span>, <span className="text-white/80">offtake</span>,{" "}
              <span className="text-white/80">partnerships</span>, <span className="text-white/80">supply & EPC coordination</span>,{" "}
              <span className="text-white/80">careers</span>.
            </div>
          </div>

          {/* Toast */}
          {status.type !== "idle" && (
            <div
              className={cx(
                "mt-4 rounded-2xl border px-4 py-3 text-sm backdrop-blur",
                status.type === "success"
                  ? "border-emerald-300/20 bg-emerald-400/10 text-emerald-100"
                  : "border-rose-300/20 bg-rose-400/10 text-rose-100"
              )}
            >
              {status.message}
            </div>
          )}
        </div>

        {/* Right: form */}
        <div className="lg:col-span-3">
          <form onSubmit={onSubmit} className="rounded-3xl border border-white/10 bg-black/25 p-6 backdrop-blur">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-xs font-medium text-white/70">Name *</span>
                <input
                  value={form.name}
                  onChange={onChange("name")}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-white/10"
                  placeholder="Your name"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-white/70">Email *</span>
                <input
                  value={form.email}
                  onChange={onChange("email")}
                  type="email"
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-white/10"
                  placeholder="you@company.com"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-white/70">Company</span>
                <input
                  value={form.company}
                  onChange={onChange("company")}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-white/10"
                  placeholder="Company / Organization"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-white/70">Topic</span>
                <select
                  value={form.topic}
                  onChange={onChange("topic")}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-white/10"
                >
                  <option>Partnership</option>
                  <option>Land Opportunity</option>
                  <option>Offtake</option>
                  <option>Supply / Vendor</option>
                  <option>Careers</option>
                  <option>Other</option>
                </select>
              </label>
            </div>

            <label className="mt-4 block">
              <span className="text-xs font-medium text-white/70">Message *</span>
              <textarea
                value={form.message}
                onChange={onChange("message")}
                className="mt-2 min-h-[140px] w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-white/10"
                placeholder="Tell us what you're reaching out about (land, offtake, partnership, supply, hiring) and any relevant details..."
              />
            </label>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-white/50">
                This form opens your email client. If you prefer, email us directly.
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/10 px-5 py-3 text-sm font-semibold text-white hover:bg-white/15"
              >
                Send Inquiry
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
